import {
	ChangeDetectorRef,
	Component,
	ContentChild,
	EventEmitter,
	forwardRef,
	inject,
	Input,
	Output,
	TemplateRef,
	ViewChild,
} from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { of } from "rxjs";
import { filter, map, share, switchMap, tap } from "rxjs/operators";

import { FileUploadService } from "../services/file-upload.service";

const INPUT_CONTROL_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => FileInputComponent),
	multi: true,
};

@Component({
	selector: "ic-file-input",
	templateUrl: "./file-input.component.html",
	styleUrls: ["./file-input.component.scss"],
	providers: [INPUT_CONTROL_ACCESSOR],
	standalone: false,
})
export class FileInputComponent {
	private readonly uploadService = inject(FileUploadService);
	private readonly cdRef = inject(ChangeDetectorRef);

	uploading = false;
	uploaded = false;
	progress = 0;
	disabled = false;

	@Input() accept: string;
	@Input() capture: string;
	@Input() placeholder: string;
	@Input() label: string;
	@Input() help: string;
	@Input() namespace: string;
	@Input() type: string;
	@Input() isRequired = true;
	@Input() generateFilename = false;

	@Output() errorEvent = new EventEmitter<void>();

	@ViewChild("file", { static: false }) inputFile: { nativeElement: HTMLInputElement };

	@ContentChild("fileTmpl", { static: false }) fileTemplate: TemplateRef<any>;
	@ContentChild("uploadTmpl", { static: false }) uploadTemplate: TemplateRef<any>;
	@ContentChild("progressTmpl", { static: false }) progressTemplate: TemplateRef<any>;

	files: { filename: string; url: string; key?: string }[] = [];

	private onTouch = () => {}; // eslint-disable-line @typescript-eslint/no-empty-function
	private onModelChange = (_: unknown) => {};

	selectFile() {
		if (this.disabled) {
			return;
		}

		const input = this.inputFile.nativeElement;
		input.click();
	}

	onFileSelected(list: FileList) {
		if (list.length === 0) {
			this.onTouched();
			return;
		}

		this.uploading = true;

		of(list[0])
			.pipe(
				switchMap((file) => this.uploadService.upload(this.type, this.namespace, file, this.generateFilename)),
				tap((result: any) => {
					if (result.progress) {
						this.progress = Math.floor(result.progress);
						this.cdRef.detectChanges();
					}
				}),
				share(),
				filter((response: any) => !response.progress),
				map((response) => response),
				tap((response: { filename: string; url: string }) => {
					if (!response) {
						return;
					}

					this.onChange(response.filename);
					this.uploaded = true;
					this.files.push({
						...response,
						filename: list[0].name,
					});
					this.cdRef.markForCheck();
				}),
			)
			.subscribe({
				error: () => {
					this.uploading = false;
					this.files = [];
					this.errorEvent.emit();
				},
				complete: () => {
					this.onTouched();
					this.uploading = false;
					this.cdRef.markForCheck();
					this.inputFile.nativeElement.value = null;
				},
			});
	}

	reset() {
		if (this.disabled) {
			return;
		}

		this.uploaded = false;
		this.files = [];
		this.onChange("");
	}

	writeValue(value: { filename: string; url: string } | { filename: string; url: string }[]) {
		if (!value) {
			this.files = [];
			this.uploaded = false;

			return;
		}

		this.files = Array.isArray(value) ? value : [value];
		this.uploaded = true;

		this.cdRef.markForCheck();
	}

	registerOnChange(fn: (v: unknown) => void) {
		this.onModelChange = fn;
	}

	registerOnTouched(fn: () => void) {
		this.onTouch = fn;
	}

	setDisabledState(isDisabled: boolean) {
		this.disabled = isDisabled;
	}

	onChange(value: string) {
		this.onModelChange(value);
	}

	onTouched() {
		this.onTouch();
	}
}
